/**
 * Where an AI referral went after the page it landed on.
 *
 * One hop, not seven. The question a generated page has to answer is whether
 * it sent anybody deeper into the brand's site or ended the visit, and the
 * rows with no second page are the interesting half: those pages answered the
 * question completely, which is either the point of the page or a reason to
 * put a next step on it.
 */

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { api } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { SourceNote, TrafficEmpty, Unavailable } from "~/modules/analytics/pages/tracking/shared";

export function JourneysPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { locale } = useI18n();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "traffic", "journeys", filters.range],
		queryFn: () => api.trafficJourneys(entityId, { range: filters.range }),
		...read,
	});

	return (
		<StageShell
			entityId={entityId}
			reading="journeys"
			lede={t("journeys.lede")}
			filters={filters}
			setFilters={setFilters}
		>
			<Async state={state}>
				{(data) => (
					<>
						<SourceNote frame={data} />
						{!data.available || data.setup_required ? (
							<Unavailable frame={data} entityId={entityId} />
						) : (
							<Section title={t("journeys.hops")} lede={t("journeys.hopsHint")}>
								{data.steps.length === 0 ? (
									<TrafficEmpty
										title="journeys.emptyTitle"
										body="journeys.emptyBody"
										entityId={entityId}
										destination="traffic"
									/>
								) : (
									<div className="table-scroll">
										<Table variant="resource">
											<TableHeader>
												<TableRow>
													<TableHead className="wrap">{t("journeys.landed")}</TableHead>
													<TableHead className="wrap">{t("journeys.then")}</TableHead>
													<TableHead className="num">{t("traffic.sessions")}</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{data.steps.map((step) => (
													<TableRow key={`${step.from}→${step.to ?? ""}`}>
														<TableCell className="wrap mono">{step.from}</TableCell>
														<TableCell className="wrap">
															{step.to ? (
																<span className="mono">{step.to}</span>
															) : (
																// Not an empty cell: "the visit ended here"
																// is the answer, and a blank reads as
																// missing data.
																<span className="faint">{t("journeys.ended")}</span>
															)}
														</TableCell>
														<TableCell className="num">{fmt.number(locale, step.sessions)}</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</div>
								)}
							</Section>
						)}
					</>
				)}
			</Async>
		</StageShell>
	);
}
